import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { CheckInView } from "./features/CheckInView";
import { DiaryEntryEditor } from "./features/DiaryEntryEditor";
import { FoodView } from "./features/FoodView";
import { MoreView } from "./features/MoreView";
import { OnboardingView } from "./features/OnboardingView";
import { TodayView } from "./features/TodayView";
import { WeekView } from "./features/WeekView";
import { WeightView } from "./features/WeightView";
import { mealLabel } from "./meals";
import type { AppController, DiaryEntry, ExecuteAction, MealName } from "./types";
import { applyTheme, getNextTheme, getPreferredTheme, saveTheme, type AppTheme } from "./theme";
import "./styles.css";

type MainViewName = "diary" | "week" | "weight" | "more";
type ViewName = MainViewName | "food" | "check-in";

export interface DietTrackerAppProps {
  controller: AppController;
}

interface NoticeState {
  id: number;
  tone: "success" | "error";
  message: string;
}

const NAVIGATION: Array<{ view: MainViewName; label: string; mark: string }> = [
  { view: "diary", label: "Diary", mark: "D" },
  { view: "week", label: "Week", mark: "7" },
  { view: "weight", label: "Weight", mark: "W" },
  { view: "more", label: "More", mark: "..." },
];

export function DietTrackerApp({ controller }: DietTrackerAppProps) {
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  const [view, setView] = useState<ViewName>(() => snapshot.checkIn.isLoggingLocked ? "check-in" : "diary");
  const [selectedDiaryDate, setSelectedDiaryDate] = useState(snapshot.todayDate);
  const [selectedFoodMeal, setSelectedFoodMeal] = useState<MealName>("Breakfast");
  const [notice, setNotice] = useState<NoticeState>();
  const [deletingEntryId, setDeletingEntryId] = useState<string>();
  const [editingEntry, setEditingEntry] = useState<DiaryEntry>();
  const [theme, setTheme] = useState<AppTheme>(getPreferredTheme);
  const wasOnboarding = useRef(snapshot.needsOnboarding);

  useEffect(() => applyTheme(theme), [theme]);

  useEffect(() => {
    const justCompletedOnboarding = wasOnboarding.current && !snapshot.needsOnboarding;
    wasOnboarding.current = snapshot.needsOnboarding;
    if (justCompletedOnboarding) window.scrollTo({ top: 0, behavior: "auto" });
  }, [snapshot.needsOnboarding]);

  useEffect(() => {
    if (snapshot.week.days.some((day) => day.date === selectedDiaryDate)) return;
    setSelectedDiaryDate(
      snapshot.week.days.find((day) => day.isToday)?.date
        ?? snapshot.week.days.at(-1)?.date
        ?? snapshot.todayDate,
    );
  }, [selectedDiaryDate, snapshot.todayDate, snapshot.week.days]);

  useEffect(() => {
    if (!notice) return;
    const timeout = window.setTimeout(() => setNotice((current) => current?.id === notice.id ? undefined : current), 4500);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  const execute: ExecuteAction = useCallback(async <T,>(
    action: () => Promise<T>,
    options?: { successMessage?: string; errorMessage?: string },
  ) => {
    try {
      const result = await action();
      if (options?.successMessage) setNotice({ id: Date.now(), tone: "success", message: options.successMessage });
      return { ok: true, value: result };
    } catch (error) {
      const message = options?.errorMessage
        ?? (error instanceof Error && error.message ? error.message : "Something went wrong. Please try again.");
      setNotice({ id: Date.now(), tone: "error", message });
      return { ok: false };
    }
  }, []);

  const navigate = useCallback((next: ViewName) => {
    setView(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  function toggleTheme() {
    const nextTheme = getNextTheme(theme);
    setTheme(nextTheme);
    saveTheme(nextTheme);
  }

  async function deleteEntry(entry: DiaryEntry) {
    if (!window.confirm(`Remove ${entry.foodName} from ${mealLabel(entry.meal).toLowerCase()}?`)) return;
    setDeletingEntryId(entry.id);
    await execute(() => controller.deleteDiaryEntry(entry.id), { successMessage: `${entry.foodName} removed.` });
    setDeletingEntryId(undefined);
  }

  async function copyMeal(sourceDate: string, meal: MealName, targetDates: string[]): Promise<boolean> {
    const result = await execute(
      () => controller.copyMeal({ sourceDate, meal, targetDates }),
      {
        successMessage: `${mealLabel(meal)} copied to ${targetDates.length} ${targetDates.length === 1 ? "day" : "days"}.`,
      },
    );
    return result.ok;
  }

  function openFood(date: string, meal: MealName = "Breakfast") {
    setSelectedDiaryDate(date);
    setSelectedFoodMeal(meal);
    navigate("food");
  }

  function openBackfillFood() {
    const backfillDate = snapshot.checkIn.allowedBackfillDates.at(-1) ?? snapshot.todayDate;
    openFood(backfillDate);
  }

  if (snapshot.needsOnboarding) {
    return (
      <div className="app-shell app-shell--onboarding">
        <a className="skip-link" href="#main-content">Skip to content</a>
        <header className="app-header app-header--onboarding">
          <div className="wordmark" aria-label="Diet Tracker">
            <span className="wordmark__mark" aria-hidden="true">DT</span>
            <span>Diet Tracker</span>
          </div>
          <div className="app-header__actions">
            {!snapshot.isOnline && <span className="status-pill status-pill--offline">Offline setup</span>}
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
          </div>
        </header>
        <main id="main-content" className="app-main app-main--onboarding" tabIndex={-1}>
          <OnboardingView snapshot={snapshot} controller={controller} execute={execute} />
        </main>
        {notice && (
          <div className={`toast toast--${notice.tone}`} role={notice.tone === "error" ? "alert" : "status"}>
            <span aria-hidden="true">{notice.tone === "success" ? "OK" : "!"}</span>
            <p>{notice.message}</p>
            <button type="button" onClick={() => setNotice(undefined)} aria-label="Dismiss message">x</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <header className="app-header">
        <button className="wordmark" type="button" onClick={() => navigate("diary")} aria-label="Diet Tracker home">
          <span className="wordmark__mark" aria-hidden="true">DT</span>
          <span>Diet Tracker</span>
        </button>
        <div className="app-header__actions">
          {(snapshot.checkIn.status === "due" || snapshot.checkIn.status === "draft" || snapshot.checkIn.status === "overdue") && (
            <button className={snapshot.checkIn.status === "overdue" ? "header-action header-action--urgent" : "header-action"} type="button" onClick={() => navigate("check-in")}>
              {snapshot.checkIn.status === "overdue" ? "Check-in overdue" : "Check in"}
            </button>
          )}
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
        </div>
      </header>

      <main id="main-content" className="app-main" tabIndex={-1}>
        {view === "diary" && (
          <TodayView
            snapshot={snapshot}
            selectedDate={selectedDiaryDate}
            onSelectDate={setSelectedDiaryDate}
            onFindFood={openFood}
            onCheckIn={() => navigate("check-in")}
            onDeleteEntry={deleteEntry}
            onEditEntry={setEditingEntry}
            onCopyMeal={copyMeal}
            deletingEntryId={deletingEntryId}
          />
        )}
        {view === "food" && (
          <FoodView
            snapshot={snapshot}
            controller={controller}
            execute={execute}
            initialDate={selectedDiaryDate}
            initialMeal={selectedFoodMeal}
            onBack={() => navigate("diary")}
          />
        )}
        {view === "week" && <WeekView snapshot={snapshot} controller={controller} execute={execute} />}
        {view === "weight" && <WeightView snapshot={snapshot} controller={controller} execute={execute} />}
        {view === "check-in" && (
          <CheckInView
            snapshot={snapshot}
            controller={controller}
            execute={execute}
            onBackfillFood={openBackfillFood}
            onBackfillWeight={() => navigate("weight")}
          />
        )}
        {view === "more" && <MoreView snapshot={snapshot} controller={controller} execute={execute} onCheckIn={() => navigate("check-in")} />}
      </main>

      {editingEntry && (
        <DiaryEntryEditor
          snapshot={snapshot}
          entry={editingEntry}
          controller={controller}
          execute={execute}
          onCancel={() => setEditingEntry(undefined)}
          onSaved={() => setEditingEntry(undefined)}
        />
      )}

      <nav className="bottom-navigation" aria-label="Main navigation">
        {NAVIGATION.map((item) => {
          const isActive = view === item.view || (item.view === "diary" && view === "food");
          return (
            <button
              key={item.view}
              type="button"
              className={isActive ? "bottom-navigation__item bottom-navigation__item--active" : "bottom-navigation__item"}
              onClick={() => navigate(item.view)}
              aria-current={isActive ? "page" : undefined}
            >
              <span aria-hidden="true">{item.mark}</span>
              <small>{item.label}</small>
            </button>
          );
        })}
      </nav>

      {notice && (
        <div className={`toast toast--${notice.tone}`} role={notice.tone === "error" ? "alert" : "status"}>
          <span aria-hidden="true">{notice.tone === "success" ? "OK" : "!"}</span>
          <p>{notice.message}</p>
          <button type="button" onClick={() => setNotice(undefined)} aria-label="Dismiss message">x</button>
        </div>
      )}
    </div>
  );
}

function ThemeToggle({ theme, onToggle }: { theme: AppTheme; onToggle: () => void }) {
  const nextTheme = getNextTheme(theme);
  return (
    <button
      className="theme-toggle"
      type="button"
      onClick={onToggle}
      aria-label={`Switch to ${nextTheme} mode`}
      title={`Switch to ${nextTheme} mode`}
    >
      {nextTheme === "light" ? (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="3.5" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.66 6.34l1.41-1.41" />
        </svg>
      ) : nextTheme === "dark" ? (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20.3 15.1A8.4 8.4 0 0 1 8.9 3.7 8.5 8.5 0 1 0 20.3 15.1Z" />
        </svg>
      ) : (
        <span className="theme-toggle__pink-mark" aria-hidden="true">&#9829;</span>
      )}
    </button>
  );
}
